const STOREFRONT_TOKEN = '7332fbac387a7ce110901497e21b91ce';

function toSlug(str) {
  return str
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/[\u2010-\u2015]/g, '-')
    .replace(/[_\s]+/g, '-') // underscores/spaces → dash
    .replace(/[^a-z0-9-]/g, '') // remove invalid chars
    .replace(/--+/g, '-') // collapse multiple dashes
    .replace(/^-+|-+$/g, '') || '';
}

function getInitials(name) {
  if (!name) return '';
  const words = name.trim().split(' ');
  const first = words[0]?.[0] || '';
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function getStateField(item, key) {
  if (!item?.fields) return '';
  return item.fields.find(field => field.key === key)?.value || '';
}

function getReferencedColor(item, key) {
  const field = item.fields.find(stateField => stateField.key === key);
  const color = field?.referenceObject?.fields?.find(referenceField => referenceField.key === 'color')?.value;
  return color || field?.value || '';
}

function findStateInfo(data, id) {
  const slug = toSlug(id);
  if (!slug) return null;

  return data.find(item => {
    const values = [item.handle, getStateField(item, 'code'), getStateField(item, 'name')];
    return values.some(value => toSlug(value) === slug);
  }) || null;
}

function findMapInfo(data, id) {
  let info = findStateInfo(data, id);
  if (!info && (id.startsWith('ca-') || id.startsWith('tx-'))) {
    info = findStateInfo(data, id.slice(3));
  }
  return info;
}

function getStateId(info) {
  return toSlug(getStateField(info, 'code') || info.handle);
}

function withStatePrefix(prefix, id) {
  const slug = toSlug(id);
  return slug.startsWith(prefix + '-') ? slug : prefix + '-' + slug;
}

function isRegion(info, id, code, name) {
  const values = [id, info?.handle, getStateField(info, 'code'), getStateField(info, 'name')];
  return values.some(value => {
    const slug = toSlug(value);
    return slug === code || slug === name;
  });
}

function isSpecialMapRegionId(id) {
  const slug = toSlug(id);
  return slug === 'ca' || slug === 'california' || slug === 'tx' || slug === 'texas';
}

function findUsaStateGroup(element, data) {
  const usaMap = element.closest('#usa-map');
  if (!usaMap) return null;

  let group = element.closest('g[id]');
  while (group && usaMap.contains(group)) {
    if (findStateInfo(data, group.id) || isSpecialMapRegionId(group.id)) {
      return group;
    }
    group = group.parentElement?.closest('g[id]') || null;
  }

  return null;
}

function getCaliforniaCountyName(rawId) {
  return rawId
    .replace(/^_x[0-9a-fA-F]+_\d+_/, '')
    .replace(/^_x[0-9a-fA-F]+_/, '')
    .replace(/_\d+$/, '');
}

function findCaliforniaCountyElement(element, data) {
  const californiaMap = element.closest('#california-map');
  if (!californiaMap) return null;

  let target = element.closest('[id]');
  let countyElement = null;
  while (target && californiaMap.contains(target)) {
    const countyName = getCaliforniaCountyName(target.id);
    if (findStateInfo(data, withStatePrefix('ca', countyName)) || findStateInfo(data, countyName)) {
      countyElement = target;
    }
    target = target.parentElement?.closest('[id]') || null;
  }

  return countyElement;
}

function findTexasCountyElement(element) {
  return element.closest('#texas-map g[id]');
}

function getMapElementInfo(element, data = []) {
  let target = element;
  let classes = Array.from(target.classList);

  if (classes.length === 0 && (target.tagName.toLowerCase() === 'path' || target.tagName.toLowerCase() === 'rect')) {
    const parentGroup = target.closest('g');
    if (parentGroup) {
      const groupClasses = Array.from(parentGroup.classList);
      if (groupClasses.some(c => c.startsWith('state--') || c.startsWith('county--'))) {
        target = parentGroup;
        classes = groupClasses;
      }
    }
  }

  let isState = classes.some(c => c.startsWith('state--'));
  let isCounty = classes.some(c => c.startsWith('county--'));
  let id = '';

  if (isState) {
    id = toSlug(classes.find(c => c.startsWith('state--'))?.replace(/state--/i, '') || '');
    const stateInfo = findStateInfo(data, id);
    id = stateInfo ? getStateId(stateInfo) : id;
  } else if (isCounty) {
    id = toSlug(classes.find(c => c.startsWith('county--'))?.replace(/county--/i, '') || '');
    if (target.closest('#california-map')) {
      id = 'ca-' + id;
    }
  }

  if (!id) {
    const usaState = findUsaStateGroup(target, data);
    if (usaState) {
      const stateInfo = findStateInfo(data, usaState.id);
      target = usaState;
      isState = true;
      id = stateInfo ? getStateId(stateInfo) : toSlug(usaState.id);
    }
  }

  if (!id) {
    const californiaCounty = findCaliforniaCountyElement(target, data);
    if (californiaCounty) {
      const countyName = getCaliforniaCountyName(californiaCounty.id);
      const prefixedCountyName = withStatePrefix('ca', countyName);
      const countyInfo = findStateInfo(data, prefixedCountyName) || findStateInfo(data, countyName);
      target = californiaCounty;
      isCounty = true;
      id = countyInfo && toSlug(countyInfo.handle) === prefixedCountyName ? prefixedCountyName : withStatePrefix('ca', countyInfo ? getStateId(countyInfo) : countyName);
    }
  }

  if (!id) {
    const texasCounty = findTexasCountyElement(target);
    if (texasCounty) {
      target = texasCounty;
      isCounty = true;
      id = 'tx-' + toSlug(texasCounty.id);
    }
  }

  return { target, classes, isState, isCounty, id };
}

function getColorTarget(element, data) {
  if (element.closest('#usa-map')) {
    return findUsaStateGroup(element, data);
  }

  if (element.closest('#california-map')) {
    return findCaliforniaCountyElement(element, data);
  }

  if (element.closest('#texas-map')) {
    return findTexasCountyElement(element);
  }

  return null;
}

function isCaliforniaMapTextElement(svgPart, colorTarget) {
  const id = svgPart.id || '';
  const countyName = getCaliforniaCountyName(colorTarget.id || '');
  const countySlug = toSlug(countyName);

  if (!countySlug || toSlug(id) === countySlug) return false;

  return !svgPart.hasAttribute('stroke') && toSlug(getCaliforniaCountyName(id)) === countySlug;
}

function applyMapColors(element, info, data) {
  const colorTarget = getColorTarget(element, data);
  if (!colorTarget || colorTarget.dataset.colorsApplied === 'true') return;

  const mapColor = getReferencedColor(info, 'map_color');
  const mapColorOpacity = getReferencedColor(info, 'map_color_opacity');
  const textColor = getReferencedColor(info, 'map_text_color');

  if (!mapColor && !textColor) return;

  const svgParts = colorTarget.matches('path, rect')
    ? [colorTarget, ...colorTarget.querySelectorAll('path, rect')]
    : [...colorTarget.querySelectorAll('path, rect')];

  svgParts.forEach(svgPart => {
    const isCaliforniaMapPart = Boolean(svgPart.closest('#california-map'));
    const isText = isCaliforniaMapPart
      ? isCaliforniaMapTextElement(svgPart, colorTarget)
      : svgPart.id === 'DC' || /^[A-Z]{2}$/.test(svgPart.id) || /_3$/.test(svgPart.id) || /^path\d+$/.test(svgPart.id);
    if (isText) {
      if (textColor) svgPart.setAttribute('fill', textColor);
    } else if (mapColor && (!isCaliforniaMapPart || svgPart.hasAttribute('fill'))) {
      svgPart.setAttribute('fill', mapColor);
      if (mapColorOpacity) {
        svgPart.setAttribute('fill-opacity', mapColorOpacity);
      } else {
        svgPart.removeAttribute('fill-opacity');
      }
    }
  });

  colorTarget.dataset.colorsApplied = 'true';
}

/* === OPTIMIZED Data Fetching === */
async function fetchAllStateData() {
  const typeHandle = 'states';
  let hasNextPage = true;
  let endCursor = null;
  let allItems = [];

  while (hasNextPage) {
    // We request 'references' on the fields to get the partners in the SAME call
    // reducing requests from 50+ to just 1 or 2.
    const query = `
      query {
        metaobjects(type: "${typeHandle}", first: 50, after: ${endCursor ? `"${endCursor}"` : null}) {
          edges {
            node {
              handle
              fields {
                key
                value
                reference {
                  ... on MediaImage {
                    image { url }
                  }
                  ... on Metaobject {
                    handle
                    fields {
                      key
                      value
                    }
                  }
                }
                # Fetch nested partners immediately
                references(first: 50) {
                  nodes {
                    ... on Metaobject {
                      id
                      type
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image { url }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    try {
      const response = await fetch('/api/2024-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query })
      });

      const json = await response.json();
      if (!json.data) {
        console.error('Error fetching data:', json);
        break;
      }

      const metaobjects = json.data.metaobjects.edges.map(edge => edge.node);

      // Post-process the data structure to match what the popup expects
      metaobjects.forEach(mo => {
        mo.fields.forEach(f => {
          // 1. Flatten main state images
          if (f.reference?.image?.url) {
            f.value = f.reference.image.url;
          }

          if (f.reference?.fields) {
            f.referenceObject = f.reference;
          }

          // 2. Map the nested 'references' node list to 'referenceList' property
          // This replaces the old slow fetchMetaobjectsByIds loop
          if (f.key === 'select_account_partners' && f.references?.nodes) {
            f.referenceList = f.references.nodes.map(partnerNode => {
               // Process partner fields (e.g. resolve images inside partner)
               partnerNode.fields.forEach(pf => {
                  if (pf.reference?.image?.url) {
                    pf.value = pf.reference.image.url;
                  }
               });
               return partnerNode;
            });
          }
        });
      });

      allItems.push(...metaobjects);
      hasNextPage = json.data.metaobjects.pageInfo.hasNextPage;
      endCursor = json.data.metaobjects.pageInfo.endCursor;

    } catch (err) {
      console.error('Network error fetching states:', err);
      hasNextPage = false;
    }
  }

  return allItems;
}

async function loadTexasMap() {
  const texasMapContainer = document.querySelector('.texas-representative-location[data-texas-map-url]');
  if (!texasMapContainer) return;

  try {
    const response = await fetch(texasMapContainer.dataset.texasMapUrl);
    const svg = await response.text();
    texasMapContainer.innerHTML = svg;
    texasMapContainer.querySelector('svg')?.setAttribute('id', 'texas-map');
  } catch (err) {
    console.error('Error loading Texas map:', err);
  }
}

async function loadUsaMap() {
  const usaMapContainer = document.querySelector('.usa-representative-location[data-usa-map-url]');
  if (!usaMapContainer) return;

  try {
    const response = await fetch(usaMapContainer.dataset.usaMapUrl);
    const svg = await response.text();
    usaMapContainer.innerHTML = svg;
    usaMapContainer.querySelector('svg')?.setAttribute('id', 'usa-map');
  } catch (err) {
    console.error('Error loading USA map:', err);
  }
}

async function loadCaliforniaMap() {
  const californiaMapContainer = document.querySelector('.california-representative-location[data-california-map-url]');
  if (!californiaMapContainer) return;

  try {
    const response = await fetch(californiaMapContainer.dataset.californiaMapUrl);
    const svg = await response.text();
    californiaMapContainer.innerHTML = svg;
    californiaMapContainer.querySelector('svg')?.setAttribute('id', 'california-map');
  } catch (err) {
    console.error('Error loading California map:', err);
  }
}

/* === MAIN INITIALIZATION === */
Promise.all([fetchAllStateData(), loadUsaMap(), loadCaliforniaMap(), loadTexasMap()]).then(([data]) => {
  window.stateData = data;
  console.log('Loaded optimized state data:', data);

  // Handle clicks on USA states, California counties, and Texas counties
  const mapElements = document.querySelectorAll('#usa-map path, #usa-map g, #usa-map rect, #california-map path, #california-map g, #california-map rect, #texas-map path, #texas-map g, #texas-map rect');
  
  // Apply data classes and metaobject-selected colors before binding interactions.
  mapElements.forEach(el => {
    const { id } = getMapElementInfo(el, data);
    if (!id) return;

    const info = findMapInfo(data, id);

    if (info) {
      applyMapColors(el, info, data);
    }

    // California and Texas must always have main_data to allow sub-map clicks
    if (isRegion(info, id, 'ca', 'california') || isRegion(info, id, 'tx', 'texas')) {
      el.classList.add('main_data');
      return;
    }

    if (info) {
      el.classList.add('main_data');
    } else {
      el.classList.add('no_data');
    }
  });
  document.querySelectorAll('#usa-map, #california-map, #texas-map').forEach(map => {
    map.addEventListener('click', (e) => {
      const clickedElement = e.target instanceof Element ? e.target : null;
      if (!clickedElement) return;

      const { target, isState, isCounty, id } = getMapElementInfo(clickedElement, window.stateData);
      if (!id) return;

      console.log('Clicked ID (slug):', id, '| isState:', isState, '| isCounty:', isCounty);

      const californiaSection = document.querySelector('.california-representative-section');
      const texasSection = document.querySelector('.texas-representative-section');

      const revealMapSection = (sectionToShow, sectionToHide) => {
        document.querySelector('.custom-model-main').classList.remove('model-open');
        if (sectionToHide) {
          sectionToHide.style.display = 'none';
        }
        if (sectionToShow) {
          sectionToShow.style.display = 'block';
          sectionToShow.scrollIntoView({ behavior: 'smooth', block: 'start' });
          sectionToShow.classList.add('highlight-section');
          setTimeout(() => sectionToShow.classList.remove('highlight-section'), 1500);
        }
      };

      const regionInfo = findMapInfo(window.stateData, id);

      // 1. California Special Handling
      if (isRegion(regionInfo, id, 'ca', 'california')) {
        revealMapSection(californiaSection, texasSection);
        return; 
      }

      // 2. Texas Special Handling
      if (isRegion(regionInfo, id, 'tx', 'texas')) {
        revealMapSection(texasSection, californiaSection);
        return; 
      }

      // 3. Logic for Counties
      if (isCounty && target.closest('#california-map') && californiaSection) {
        californiaSection.style.display = 'block';
      }
      if (isCounty && target.closest('#texas-map') && texasSection) {
        texasSection.style.display = 'block';
      }

      // 4. Logic for Other States (Hide map sections)
      if (isState && id !== 'ca' && californiaSection) {
        californiaSection.style.display = 'none';
      }
      if (isState && id !== 'tx' && texasSection) {
        texasSection.style.display = 'none';
      }

      // 5. Build Popup Content
      // Find data for clicked state/county — try prefixed id first, fall back to unprefixed
      const info = findMapInfo(window.stateData, id);
      if (!info) {
        console.warn('No data found for:', id);
        return;
      }

      const fields = Object.fromEntries(info.fields.map(f => [f.key, f.value]));
      document.querySelector('.custom-model-head .heading').textContent = fields.region ? `${fields.region} Region` : 'Region';
      document.querySelector('.custom-model-head .sub-heading').textContent = fields.name || '';

      let partnersHTML = '';
      let eventsHTML = '';
      const partnersField = info.fields.find(f => f.key === 'select_account_partners');

      if (partnersField && partnersField.referenceList) {
        partnersField.referenceList.forEach(p => {
          const rep = {};
          p.fields.forEach(f => {
            rep[f.key] = f.value || (f.reference?.image?.url ?? '');
          });

          // Partner Card
          partnersHTML += `
          <div class="top-content-box">
            <div class="bio-items">
              <div class="image">
                ${rep.image
                  ? `<img src="${rep.image}" alt="${rep.name || ''}" width="75" height="75">`
                  : `<div class="initials-fallback">${getInitials(rep.name)}</div>`}
              </div>
              <div class="content">
                <h4>${rep.name || ''}</h4>
                <p>${rep.partner_type || ''}</p>
                ${rep.email ? `
                  <a href="mailto:${rep.email}" class="email">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.0026 2H14.0026C14.3708 2 14.6693 2.29848 14.6693 2.66667V13.3333C14.6693 13.7015 14.3708 14 14.0026 14H2.0026C1.63442 14 1.33594 13.7015 1.33594 13.3333V2.66667C1.33594 2.29848 1.63442 2 2.0026 2ZM13.3359 4.82528L8.05047 9.55867L2.66927 4.81063V12.6667H13.3359V4.82528ZM3.01024 3.33333L8.04387 7.77467L13.0033 3.33333H3.01024Z" fill="black"></path>
                    </svg>
                    <span>${rep.email}</span>
                  </a>` : ''}
                ${rep.view_bio ? `
                  <a class="bio" href="${rep.view_bio}">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.0026 14.6665C4.3207 14.6665 1.33594 11.6817 1.33594 7.9998C1.33594 4.3179 4.3207 1.33313 8.0026 1.33313C11.6845 1.33313 14.6693 4.3179 14.6693 7.9998C14.6693 11.6817 11.6845 14.6665 8.0026 14.6665ZM8.0026 13.3331C10.9481 13.3331 13.3359 10.9453 13.3359 7.9998C13.3359 5.05428 10.9481 2.66646 8.0026 2.66646C5.05708 2.66646 2.66927 5.05428 2.66927 7.9998C2.66927 10.9453 5.05708 13.3331 8.0026 13.3331ZM7.33594 4.66646H8.66927V5.9998H7.33594V4.66646ZM7.33594 7.33313H8.66927V11.3331H7.33594V7.33313Z" fill="black"></path>
                    </svg>
                    <span>Read Full Bio</span>
                  </a>` : ''}
              </div>
            </div>
          </div>`;
        });

        // Collect all class items
        let allClasses = [];
        partnersField.referenceList.forEach(p => {
          const rep = {};
          p.fields.forEach(f => {
            rep[f.key] = f.value || (f.reference?.image?.url ?? '');
          });

          // Loop through 10 possible classes for each partner
          for (let i = 1; i <= 10; i++) {
            const infoKey = `education_class_info_class_${i}`;
            const linkKey = `education_class_link_class_${i}`;
            const classInfo = rep[infoKey], classLink = rep[linkKey];
            if (classInfo || classLink) {

              allClasses.push({
                html: `
                <div class="meet-up-item">
                  <div class="details">
                    <div class="join-class">
                      <p>${classInfo || ''}</p>
                      <a href="${classLink || '#'}" target="_blank">Sign Up</a>
                    </div>
                  </div>
                </div>`
              });
            }
          }
        });

        // Show first 3 visible, hide rest
        if (allClasses.length > 0) {
          const visible = allClasses.slice(0, 3).map(c => c.html).join('');
          const hidden = allClasses.slice(3).map(c => c.html).join('');
          
          eventsHTML += visible;
          if (allClasses.length > 3) {
            eventsHTML += `
              <div class="hidden-classes" style="display:none;">
                ${hidden}
              </div>
              <a href="#" class="view-all">View All</a>`;
          }
        }
      }

      document.querySelector('.model-content-inner').innerHTML = partnersHTML;
      document.querySelector('.bottom-content-box').innerHTML =
        `<h3>Upcoming Education</h3>${eventsHTML || '<p>No upcoming classes</p>'}`;

      document.querySelector('.custom-model-main').classList.add('model-open');
    });
  });

  // Close popup
  document.querySelectorAll('.close-btn, .bg-overlay').forEach(el =>
    el.addEventListener('click', () =>
      document.querySelector('.custom-model-main').classList.remove('model-open')
    )
  );

  // Handle View All click
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.view-all');
    if (btn) {
      e.preventDefault();
      const hidden = btn.previousElementSibling;
      if (hidden && hidden.classList.contains('hidden-classes')) {
        hidden.style.display = 'flex';
        btn.remove();
      }
    }
  });
});
